import { useEffect, useState, useCallback } from "react";
import api from "./api";
import { useNavigate } from "react-router-dom";
import tokenManager from "./tokenManager";

/**
 * Hilfsfunktion: Holt die Anmeldedaten aus dem TokenManager.
 * Gibt true wieder wenn Token gesetzt ist
 * Token wird im Backend geprüft
 */
const getCredentials = () => {
  try {
    const jwt = tokenManager.getToken();
    if (!jwt) return null;

    // // Rückgabe als URL-Parameter-String
    return true;
  } catch (error) {
    console.error("Fehler beim Parsen der Credentials:", error);
    return null;
  }
};

/**
 * Custom-Hook für GET-Anfragen.
 * Führt den Request automatisch beim Mounten oder bei URL-Änderung aus.
 */
const useDataFetch = ({ url = null, aBody = null } = {}) => {
  const navigate = useNavigate();
  const finalUrl = url ? url : ""; // api Instanz hat baseURL

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const bodyString = aBody ? JSON.stringify(aBody) : null;

  // Die eigentliche Fetch-Logik
  const fetchData = useCallback(async () => {
    const creds = getCredentials();
    if (!creds) {
      navigate("/auth");
      return;
    }

    setLoading(true);
    setError(null); // Reset Error bei neuem Versuch

    try {
      // console.log(aBody)
      const response = await api.get(finalUrl, {
        params: aBody
      });

      setData(response.data);
    } catch (err) {
      console.log(finalUrl, aBody)
      // Prüfen, ob der Server eine Antwort gesendet hat (z.B. 401)
      if (err.response) {
        const { data } = err.response;
        setError(data.message || "Ein Fehler ist aufgetreten");
      } else {
        console.log(err?.message || err)
        setError(err?.message || "Ein Problem ist aufgetreten");
      }
    } finally {
      setLoading(false);
    }
  }, [finalUrl, bodyString, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effekt: Wird ausgelöst, sobald sich die URL ändert
  useEffect(() => {
    if (url) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // Rückgabe von Status-Variablen und der manuellen refetch-Funktion
  return { data, setData, error, isLoading, refetch: fetchData };
};

export default useDataFetch;

/**
 * Custom-Hook für POST-Anfragen (z.B. für Formulare oder Login).
 * Wird manuell durch Aufruf von 'postData' ausgelöst.
 */
export const usePostDataFetch = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [data, setData] = useState(null);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const postData = useCallback(async ({ url = null, aBody = null, skipAuth = false } = {}) => {
    let finalUrl = url ? apiUrl + url : apiUrl;
    if (!skipAuth && !getCredentials()) {
      navigate("/auth");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // console.log(aBody)
      // console.log(finalUrl)
      const res = await api.post(finalUrl, aBody);
      console.log("res: ", res)
      setData(res.data);
      setResponse(res);
    } catch (err) {
      if (err.response) {
        console.log(finalUrl, aBody)
        const serverError = err.response.data.message || err.response.data.error;
        setError(serverError || "Fehler beim Senden der Daten");
        setResponse(err.response);
      } else {
        setError("Netzwerkfehler: Server nicht erreichbar");
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, navigate]);

  return { data, response, error, isLoading, postData };
};

/**
 * Custom-Hook für PUT-Anfragen (z.B. für Updates).
 * Wird manuell durch Aufruf von 'putData' ausgelöst.
 */
export const usePutDataFetch = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [data, setData] = useState(null);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const putData = useCallback(async ({ url = null, aBody = null, skipAuth = false } = {}) => {
    let finalUrl = url ? apiUrl + url : apiUrl;
    if (!skipAuth && !getCredentials()) {
      navigate("/auth");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      console.log(aBody)
      const res = await api.put(finalUrl, aBody);
      setData(res.data);
      setResponse(res);
    } catch (err) {
      if (err.response) {
        const serverError = err.response.data.message || err.response.data.error;
        setError(serverError || "Fehler beim Senden der Daten");
        setResponse(err.response);
      } else {
        setError("Netzwerkfehler: Server nicht erreichbar");
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, navigate]);

  return { data, response, error, isLoading, putData };
};

/**
 * Custom-Hook für DELETE-Anfragen.
 * Wird manuell durch Aufruf von 'deleteData' ausgelöst.
 */
export const useDeleteDataFetch = () => {
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [data, setData] = useState(null);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);

  const deleteData = useCallback(async ({ url = null, aBody = null, skipAuth = false } = {}) => {
    let finalUrl = url ? apiUrl + url : apiUrl;
    if (!skipAuth && !getCredentials()) {
      navigate("/auth");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.delete(finalUrl, { data: aBody });
      setData(res.data);
      setResponse(res);
    } catch (err) {
      if (err.response) {
        const serverError = err.response.data.message || err.response.data.error;
        setError(serverError || "Fehler beim Löschen der Daten");
        setResponse(err.response);
      } else {
        setError("Netzwerkfehler: Server nicht erreichbar");
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, navigate]);

  return { data, response, error, isLoading, deleteData };
};
